import React from 'react';
import { NavigateFunction } from 'react-router';

import { CanonicalTable } from '@/components/table/CanonicalTable';
import { STICKY_ACTIONS_COLUMN_CLASS } from '@/components/table/constants';
import { ColumnsConfigItem } from '@/components/table/sortableColumns/types';
import { Table } from '@/components/table/Table';
import { Columns, TableActions } from '@/components/table/types';
import { HOTKEY_SCOPES } from '@/hooks/hotkeys';
import { Filters } from '@/hooks/useAssets';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { getAssetActionOptions } from '@/sections/asset/config/actionOptions';
import { AssetActionsHandlers } from '@/sections/asset/types/table.types';
import { getAssetNoDataConfig } from '@/sections/asset/utils/getAssetNoDataConfig';
import { useAuth } from '@/state/auth';
import { useBulkJobsStore } from '@/state/bulkJobs.store';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';
import { Asset } from '@/types';
import { cn } from '@/utils/classname';

import { CellFiltersProvider } from './cells/CellFiltersContext';

// Revertible migration flag (plan.md Task 10). Flip to `false` to instantly roll
// back to the bespoke Table. Both components share the same TableProps<T> API, so
// this is a true one-line toggle with zero prop changes.
const USE_CANONICAL_TABLE = true;

/**
 * Props for AssetTable component
 */
export interface AssetTableProps {
  // Asset data
  assets: Asset[];
  assetsStatus: 'pending' | 'error' | 'success';
  assetsError: Error | null;
  fetchNextPage: () => void;
  isFetching?: boolean;
  isFetchingNextPage: boolean;
  assetsHasNextPage: boolean;
  isAllEmpty: boolean;
  isQueryError: boolean;

  // Column configuration
  columns: Columns<Asset>;
  activeColumns: string[] | undefined;
  setActiveColumns: (columns: string[]) => void;
  placeholder: string;
  sortableColumnsConfig: {
    fixed: ColumnsConfigItem[];
    active: ColumnsConfigItem[];
    available: ColumnsConfigItem[];
  };

  // Selection (key-based: contains asset keys, not indices)
  selectedRows: string[];
  setSelectedRows: (rows: string[]) => void;

  // Filters and sort
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;

  // Actions and handlers
  handlers: AssetActionsHandlers;
  handleKeyboardAction: (code: string, assets: Asset[]) => void;
  setIsCommentModalOpen: (open: boolean) => void;
  setIsTagModalOpen: (open: boolean) => void;
  /**
   * Callback for removing scan profile from a single row asset.
   * Provided by the parent (index.tsx) using useBulkAction — routes through
   * /bulk/asset because per-item PUT is broken for profile clears (MergeFields
   * skips empty-string Profile assignments).
   */
  onRowRemoveProfile?: (assetKey: string) => void;

  // Navigation and state
  navigate: NavigateFunction;
  resetQueryParams: () => void;
  drawerState: {
    handleOpenAssetDrawer: (
      asset?: Asset,
      isActiveDirectory?: boolean,
      osint?: boolean
    ) => void;
  };

  // Empty state callback
  /** Callback when "Get Started" button is clicked (first-time user onboarding) */
  onGetStartedClick?: () => void;

  // Drawer-specific props
  /** Table name for storage key (column preferences) */
  name?: string;
  /**
   * Enable the responsive column-hiding/compression engine. The asset table no
   * longer uses it in any context — `Assets` always passes `false` so the table
   * renders all active columns at full registry width and scrolls horizontally
   * (matching the affected-assets drawer table). The shared default stays `true`
   * for the ~37 other table consumers that still rely on responsive mode.
   */
  responsive?: boolean;
  /** Hide bulk actions and selection */
  hideBulkActions?: boolean;
  /**
   * Rendered inside a drawer (embedded affected-assets table). When true, the
   * row-action hotkeys (f/u/d) bind to the `overlay` scope so they keep working
   * for the table the user is viewing inside the drawer — `global` is suppressed
   * while any drawer is open, which would otherwise make them inert.
   */
  inDrawer?: boolean;
  /**
   * When true, a confirmation dialog opened by a prior row-action shortcut is
   * showing, so the row-action hotkeys go inert until it closes.
   */
  confirmOpen?: boolean;
}

/**
 * Asset Table Component
 *
 * Displays the main asset table with all interactive features including:
 * - Column configuration and sorting
 * - Row selection and bulk actions
 * - Keyboard shortcuts
 * - Pagination and infinite scrolling
 * - Error states and empty states
 * - Row actions menu
 *
 * @param props - Asset table props
 * @returns Asset table component
 */
export const AssetTable: React.FC<AssetTableProps> = ({
  assets,
  assetsStatus,
  assetsError,
  fetchNextPage,
  isFetching,
  isFetchingNextPage,
  assetsHasNextPage,
  isAllEmpty,
  isQueryError,
  columns,
  activeColumns,
  setActiveColumns,
  placeholder,
  sortableColumnsConfig,
  selectedRows,
  setSelectedRows,
  filters,
  setFilters,
  handlers,
  handleKeyboardAction,
  setIsCommentModalOpen,
  setIsTagModalOpen,
  navigate,
  resetQueryParams,
  drawerState,
  onGetStartedClick,
  name = 'assets',
  responsive = true,
  hideBulkActions = false,
  inDrawer = false,
  confirmOpen = false,
  onRowRemoveProfile,
}) => {
  const { isPraetorianUser } = useAuth();
  const isKeyPending = useBulkJobsStore(state => state.isKeyPending);
  const {
    modal: { selectUserAgent },
  } = useGlobalState();
  const { isOsint } = useOsintContext();
  const { canWriteAssets } = useEntitlements();

  // Determine if there are active filters (search or advancedFilters)
  const hasActiveFilters = useHasActiveFilters(filters);

  const noDataConfig = getAssetNoDataConfig({
    isQueryError,
    isAllEmpty,
    onResetQuery: resetQueryParams,
    navigate,
    hasActiveFilters,
    onClearFilters: resetQueryParams,
    onGetStartedClick,
  });

  // Revertible migration (plan.md Task 10): both components implement the same
  // TableProps<Asset> API, so the prop block below is passed through unchanged.
  const TableComponent = USE_CANONICAL_TABLE ? CanonicalTable : Table;

  return (
    <CellFiltersProvider value={filters}>
      <TableComponent
        responsive={responsive}
        tableClassName={cn('w-full', STICKY_ACTIONS_COLUMN_CLASS)}
        sortableColumns={{
          activeColumns,
          setActiveColumns,
          defaultConfig: sortableColumnsConfig,
          placeholder,
        }}
        keyboardNavigation={true}
        selection={
          hideBulkActions
            ? { hide: true }
            : {
                value: selectedRows,
                onChange: setSelectedRows,
                selectAll: true,
              }
        }
        rowActions={
          isOsint || !canWriteAssets
            ? undefined // No row actions menu in OSINT mode or without write permission
            : (asset: Asset): TableActions => {
                return {
                  menu: {
                    items: getAssetActionOptions({
                      assets: asset ? [asset] : [],
                      onCommentClick: () => setIsCommentModalOpen(true),
                      onTagClick: () => {
                        // Select this asset by key if not already selected
                        if (!selectedRows.includes(asset.key)) {
                          setSelectedRows([asset.key]);
                        }
                        setIsTagModalOpen(true);
                      },
                      handlers,
                      selectUserAgent,
                      isPraetorianUser,
                      onRemoveProfileClick:
                        asset?.profile && onRowRemoveProfile
                          ? () => onRowRemoveProfile(asset.key)
                          : undefined,
                    }).map(option => ({
                      ...option,
                      value: option.value?.toString(),
                    })),
                  },
                };
              }
        }
        onRowClick={
          isOsint
            ? undefined // No click handler in OSINT mode
            : asset => {
                drawerState.handleOpenAssetDrawer(asset, false, undefined);
              }
        }
        sort={{
          orderBy: filters.sort?.orderBy || 'identifier',
          descending: filters.sort?.descending || false,
          onSort: (sort: { orderBy: keyof Asset; descending: boolean }) => {
            setFilters(prev => ({ ...prev, sort }));
          },
        }}
        rowHeight={64}
        name={name}
        columns={columns}
        data={assets}
        rowClassName={(asset: Asset) =>
          isKeyPending(asset.key) ? 'opacity-70 cursor-wait' : ''
        }
        getRowProps={(asset: Asset) =>
          isKeyPending(asset.key) ? { 'aria-busy': true } : {}
        }
        error={assetsError}
        status={assetsStatus}
        isFetching={isFetching}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={assetsHasNextPage}
        noData={noDataConfig}
        keyboardActions={{
          altKey: false,
          codes: ['KeyF', 'KeyU', 'KeyD'],
          onKeyboardAction: handleKeyboardAction,
          enabled: !confirmOpen,
          // In-drawer affected-assets table: use the never-suppressed `overlay`
          // scope so f/u/d keep working while the drawer is open. The top-level
          // /assets table keeps the default `global` scope (suppressed by any
          // open overlay), so the background table stays inert behind a drawer.
          scope: inDrawer ? HOTKEY_SCOPES.overlay : HOTKEY_SCOPES.global,
        }}
      />
    </CellFiltersProvider>
  );
};
